import { useCallback, useEffect, useRef, useState } from 'react';
import { ResolvePluginRegistryUseCase } from '@/application/use-cases/ResolvePluginRegistryUseCase';
import { HttpPluginRegistryRepository } from '@/infrastructure/repositories/HttpPluginRegistryRepository';
import type { DeviceSession } from '@/domain/entities/DeviceSession';
import type { PluginRegistryEntry } from '@/domain/entities/PluginRegistryEntry';
import { MAX_LOAD_ATTEMPTS, waitBackoff } from '@/presentation/hooks/pluginLoadBackoff';

const resolvePluginRegistryUseCase = new ResolvePluginRegistryUseCase(
  new HttpPluginRegistryRepository()
);

/** Estado renderizável (inclui as fases transitórias de retry, para as
 * telas de loading/waiting). */
export type PluginRegistryResolutionState =
  | { kind: 'idle' }
  | { kind: 'attempting'; attempt: number }
  | { kind: 'waiting'; attempt: number; secondsRemaining: number }
  | { kind: 'resolved'; entry: PluginRegistryEntry }
  | { kind: 'not-configured' }
  | { kind: 'session-expired'; expiredAt: Date }
  | { kind: 'exhausted'; offline: boolean };

/** Só os desfechos terminais — devolvido por `resolve`/`retryManually` para
 * que o orquestrador do fluxo (`useActivationFlow`) decida o próximo
 * `FlowStep` no mesmo encadeamento imperativo de `runLoadConfig` (mesmo
 * padrão de `LoadEventConfigUseCase`/`useEventConfig.loadConfig`), sem
 * precisar de um `useEffect` só para sincronizar os dois estados. */
export type PluginRegistryResolutionOutcome =
  | { kind: 'resolved'; entry: PluginRegistryEntry }
  | { kind: 'not-configured' }
  | { kind: 'session-expired'; expiredAt: Date }
  | { kind: 'exhausted'; offline: boolean };

export interface UsePluginRegistryResolutionResult {
  state: PluginRegistryResolutionState;
  /** Inicia a resolução (CA-01) — chamado explicitamente pelo orquestrador
   * do fluxo, nunca por um `useEffect` de montagem: um `useEffect` que
   * dispara fetch no corpo seria invocado 2x pelo `React.StrictMode` em
   * dev/teste (mesma armadilha do double-POST da HUB-119, ver Retro Sprint
   * 4). */
  resolve: (session: DeviceSession, activationType: string) => Promise<PluginRegistryResolutionOutcome>;
  /** CA-16 (exaurido): reseta o contador e tenta de novo do zero. */
  retryManually: (session: DeviceSession, activationType: string) => Promise<PluginRegistryResolutionOutcome>;
}

/**
 * Resolve o registro de plugin do tipo de ativação atual (HUB-237, CA-01/
 * CA-11/CA-12/CA-16, ver ADR-010), com retry automático em backoff (1s/2s/
 * 4s) quando a falha é de rede ou outra falha transiente — nunca quando o
 * tipo simplesmente não está cadastrado (CA-11, terminal e imediato).
 */
export function usePluginRegistryResolution(): UsePluginRegistryResolutionResult {
  const [state, setState] = useState<PluginRegistryResolutionState>({ kind: 'idle' });
  const cancelledRef = useRef(false);

  // `React.StrictMode` roda setup -> cleanup -> setup na montagem inicial
  // (dev/teste) — sem o reset explícito no setup, o cleanup simulado deixa
  // `cancelledRef.current` travado em `true` para sempre, fazendo a
  // primeira chamada real a `resolve()` abortar silenciosamente (achado
  // desta fatia, mesma classe de armadilha do double-POST da HUB-119).
  useEffect(() => {
    cancelledRef.current = false;
    return (): void => {
      cancelledRef.current = true;
    };
  }, []);

  const runAttempt = useCallback(
    async (
      session: DeviceSession,
      activationType: string,
      attempt: number
    ): Promise<PluginRegistryResolutionOutcome> => {
      setState({ kind: 'attempting', attempt });
      const result = await resolvePluginRegistryUseCase.execute(session, activationType);
      if (cancelledRef.current) return { kind: 'not-configured' };

      if (result.success) {
        const outcome: PluginRegistryResolutionOutcome = { kind: 'resolved', entry: result.entry };
        setState(outcome);
        return outcome;
      }
      if (result.reason === 'not-configured') {
        const outcome: PluginRegistryResolutionOutcome = { kind: 'not-configured' };
        setState(outcome);
        return outcome;
      }
      if (result.reason === 'session-expired') {
        const outcome: PluginRegistryResolutionOutcome = {
          kind: 'session-expired',
          expiredAt: result.expiredAt,
        };
        setState(outcome);
        return outcome;
      }

      const offline = result.reason === 'network';
      if (attempt >= MAX_LOAD_ATTEMPTS) {
        const outcome: PluginRegistryResolutionOutcome = { kind: 'exhausted', offline };
        setState(outcome);
        return outcome;
      }

      await waitBackoff(
        attempt,
        (secondsRemaining) => setState({ kind: 'waiting', attempt, secondsRemaining }),
        () => cancelledRef.current
      );
      if (cancelledRef.current) return { kind: 'exhausted', offline };
      return runAttempt(session, activationType, attempt + 1);
    },
    []
  );

  const resolve = useCallback(
    (session: DeviceSession, activationType: string): Promise<PluginRegistryResolutionOutcome> =>
      runAttempt(session, activationType, 1),
    [runAttempt]
  );

  return { state, resolve, retryManually: resolve };
}
